<template>
  <div class="px-6 py-10 min-h-screen">
    <div class="mx-auto">

      <!-- Header: Back + Account Info + Balances -->
      <div class="flex items-center justify-between mb-8">
        <!-- Left side: back + account selector -->
        <div class="flex flex-col items-left">
          <!-- Back arrow -->
          <button class="text-primary hover:text-orange-600 transition-colors">
            <component :is="ArrowLeft" class="w-6 h-6"/>
          </button>

          <!-- Account info -->
          <div class="mt-4 flex flex-col">
            <label for="account-select" class="text-gray-600 text-sm mb-1">Select Account</label>
            <select
                id="account-select"
                v-model="selectedAccountId"
                class="border rounded px-3 py-2 w-64"
            >
              <option v-for="acc in accounts" :key="acc.id" :value="acc.id">
                {{ acc.account_name }} - {{ acc.account_number }} ({{ acc.currency }})
              </option>
            </select>

            <!-- Account number badge -->
            <div v-if="selectedAccount" class="mt-2">
              <span class="inline-block bg-gray-800 text-white text-sm px-3 py-1 rounded-full">
                {{ selectedAccount.account_number }}
              </span>
            </div>
          </div>
        </div>

        <!-- Right side: balances -->
        <div class="flex space-x-8" v-if="selectedAccount">
          <!-- Accounting balance -->
          <div class="text-right">
            <div class="text-lg font-medium text-gray-800">
              {{ selectedAccount.currency }} {{ formatNumber(selectedAccount.accounting_balance) }}
            </div>
            <div class="text-sm text-primary">Accounting</div>
          </div>

          <!-- Available balance -->
          <div class="text-right">
            <div class="text-lg font-medium text-gray-800">
              {{ selectedAccount.currency }} {{ formatNumber(selectedAccount.available_balance) }}
            </div>
            <div class="text-sm text-primary">Available</div>
          </div>
        </div>
      </div>

      <!-- Boxes / Sections -->
      <div class="p-6">
        <div class="flex justify-center space-x-4 mb-6">
          <button
              v-for="item in items"
              :key="item.id"
              :class="[
              'flex flex-col items-center bg-white px-6 py-4 border rounded-lg shadow-md cursor-pointer transition-colors hover:border-gray-400 hover:bg-white hover:shadow-md hover:text-gray-800',
              selected === item.id
                ? 'border-gray-400 bg-white shadow-md'
                : 'border-transparent text-primary'
            ]"
              @click="selected = item.id"
          >
            <component :is="item.icon" alt="" class="w-8 h-8 mb-2"/>
            <span :class="selected === item.id ? 'text-gray-800' : 'text-orange-400'" class="text-sm">
              {{ item.label }}
            </span>
          </button>
        </div>

        <!-- Selected Component -->
        <div class="">

          <!-- Steps -->
          <div class="flex md:flex-row md:justify-between md:items-center md:border-b md:border-gray-200 mb-8 flex-col items-start gap-5">
            <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
              <div
                  :class="['w-8 h-8 flex items-center justify-center rounded-full font-bold z-10', activeStep === 1 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">
                1
              </div>
              <span
                  :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 1 }]">Fill Data</span>
            </div>

            <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
              <div
                  :class="['w-8 h-8 flex items-center justify-center rounded-full z-10', activeStep === 2 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">
                2
              </div>
              <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 2 }]">Check the Operation</span>
            </div>

            <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
              <div
                  :class="['w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-700 rounded-full z-10', activeStep === 3 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">
                3
              </div>
              <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 3 }]">Confirmation</span>
            </div>
          </div>


          <!-- Transfer form -->
          <Transition name="slide-left">
            <div v-if="!isPanelOpen && !showTransactionConfirm && selected === 'european'" class="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div class="space-y-6">
                <div>
                  <h3 class="text-sm font-medium text-gray-600 mb-4 tracking-wide">TRANSFER TO WHOM?</h3>
                  <div class="relative">
                    <input
                        v-model="searchBeneficiary"
                        @input="debouncedSearch"
                        class="w-full px-4 py-3 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Search Beneficiary"
                        type="text"
                    />
                    <svg class="absolute left-3 top-3.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor"
                         viewBox="0 0 24 24">
                      <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke-linecap="round"
                            stroke-linejoin="round" stroke-width="2"></path>
                    </svg>
                  </div>

                  <div class="mb-6">
                    <BeneficiarySelector
                        v-model="selectedBeneficiary"
                        :beneficiaries="beneficiaryList"
                        @select-beneficiary="onBeneficiarySelected"
                        @add-new-beneficiary="openNewBeneficiaryForm"
                    />
                    <NewBeneficiaryModal
                        :isOpen="isModalOpen"
                        @close="isModalOpen = false"
                        @submitted="searchBeneficiaries"
                        type="european"/>
                  </div>
                </div>

                <!-- Account to Credit -->
                <div>
                  <h3 class="text-sm font-medium text-gray-600 mb-4 tracking-wide">ACCOUNT TO CREDIT</h3>
                  <div class="space-y-4">
                    <div>
                      <FloatingLabelInput v-model="iban" input-id="iban-field" label="IBAN *"/>
                      <p v-if="errors.iban" class="text-sm text-red-500 mt-1">{{ errors.iban }}</p>
                    </div>

                    <div>
                      <FloatingLabelInput v-model="swiftCode" input-id="swift-field" label="BIC / SWIFT Code *"/>
                      <p v-if="errors.swift" class="text-sm text-red-500 mt-1">{{ errors.swift }}</p>
                    </div>

                    <div>
                      <FloatingLabelInput v-model="beneficiaryName" input-id="name-field" label="Beneficiary name *"/>
                      <p v-if="errors.beneficiaryName" class="text-sm text-red-500 mt-1">{{ errors.beneficiaryName }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="space-y-6">
                <!-- How Much -->
                <div>
                  <h3 class="text-sm font-medium text-gray-600 mb-4 tracking-wide">HOW MUCH?</h3>
                  <div class="flex items-center gap-x-2 relative">
                    <FloatingLabelInput
                        v-model="transferValue"
                        class="w-full"
                        label="Value *"
                        min="10"
                        max="50000"
                        type="number"
                        @blur="validateTransferValue"
                    />
                    <button class="text-gray-600 bg-gray-200 rounded px-3 py-3" type="button">{{ selectedAccount?.currency }}</button>
                  </div>
                  <p v-if="errors.transferValue || transferValueError" class="text-sm text-red-500 mt-1">
                    {{ errors.transferValue || transferValueError }}
                  </p>
                  <p class="text-sm text-gray-500 mt-1">
                    For transfers over 50,000 {{ selectedAccount?.currency }} please contact your account manager.
                  </p>

                  <div class="mt-4">
                    <FloatingLabelInput v-model="description" input-id="desc-field" label="Description *"/>
                    <p v-if="errors.description" class="text-sm text-red-500 mt-1">{{ errors.description }}</p>
                  </div>
                  <div class="mt-4">
                    <textarea
                        v-model="additionalInfo"
                        class="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                        placeholder="Additional Information"
                        rows="4"
                    ></textarea>
                  </div>
                  <div class="mt-4">
                    <FloatingLabelSelect v-model="transferNature" label="What is the nature of your fund transfer? *">
                      <option value="other-services-companies">Other services provided by companies</option>
                      <option value="non-resident-transfers">Non-resident transfers (any motive)</option>
                      <option value="personnel-services">Other services of personnel nature</option>
                      <option value="emigrant-account-transfer">Transfer between emigrant accounts (Portugal and exterior)</option>
                      <option value="education-services">Education services</option>
                      <option value="export-import-merchandise">Export/Import of merchandise</option>
                      <option value="private-current">Private current transfers from residents to non-residents</option>
                      <option value="resident-account-transfer">Transfers between resident accounts, in Portugal and exterior</option>
                      <option value="travel-tourism">Travel and tourism - travel expenses and tourism stays</option>
                    </FloatingLabelSelect>
                    <p v-if="errors.transferNature" class="text-sm text-red-500 mt-1">{{ errors.transferNature }}</p>
                  </div>
                </div>

                <div>
                  <h3 class="text-sm font-medium text-gray-600 mb-2 tracking-wide">SUPPORTING TRANSFER DOCUMENTS (OPTIONAL)</h3>
                  <p class="text-sm text-gray-500 mb-4">You can upload multiple documents one by one</p>

                  <!-- List of attached documents -->
                  <div v-if="attachedDocuments.length > 0" class="mb-4 space-y-2">
                    <div v-for="(doc, index) in attachedDocuments" :key="index" class="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <div class="flex-1 mr-2">
                        <p class="text-gray-700 text-sm font-medium">{{ doc.file.name }}</p>
                        <input
                            v-model="doc.description"
                            type="text"
                            class="w-full mt-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                            placeholder="Document description"
                        />
                      </div>
                      <button type="button" class="text-red-500 px-2 py-1 rounded hover:bg-red-50" @click="removeDocument(index)">
                        <component :is="Trash" class="w-5 h-5"/>
                      </button>
                    </div>
                  </div>

                  <!-- File selection form -->
                  <Transition name="slide-down">
                    <div v-if="showDocumentForm" class="flex items-center gap-2 mb-4">
                      <div class="w-full space-y-3 transition duration-900">
                        <FloatingLabelInput v-model="documentDescription" input-id="doc-field" label="Document description"/>
                        <div class="relative">
                          <FloatingLabelFileInput
                              :key="fileInputKey"
                              ref="fileInput"
                              @change="handleFileSelect"
                              accept=".pdf,.jpg,.jpeg,.png,.bmp"
                              class="mb-4"
                              label="Select File"
                          />
                        </div>
                      </div>
                      <button class="p-4 rounded-lg shadow text-red-400 hover:bg-red-50" @click="toggleDocumentForm(false)">
                        <component :is="Trash"/>
                      </button>
                    </div>
                  </Transition>

                  <!-- Add new file button -->
                  <button
                      class="w-full flex items-center justify-center px-4 py-2 border border-orange-500 text-primary rounded-md hover:bg-orange-50 transition-colors"
                      @click="toggleDocumentForm(true)"
                  >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"></path>
                    </svg>
                    Attach Documents
                  </button>
                  <p class="text-xs text-gray-400 mt-2">Allowed file formats: pdf, jpg, jpeg, png, bmp</p>
                  <p v-if="errors.documentFile" class="text-sm text-red-500 mt-1">{{ errors.documentFile }}</p>
                </div>

              </div>
            </div>
          </Transition>
        </div>
      </div>

      <!-- Slide Panel -->
      <TransactionData
          :key="transactionDataKey"
          v-show="isPanelOpen && !showTransactionConfirm && selected === 'european' && otpStep" v-model="isPanelOpen"   :transactionData="transactionData"  @otp-verified="onOtpVerified"/>
      <Transition name="slide-right">
        <TransactionConfirm v-show="showTransactionConfirm && selected === 'european' && isPanelOpen "
                            :loading="isLoading"
                            :transactionData="transactionDataServer"
                            :transactionId="transactionId"
                            :selectedAccount="selectedAccount"
                            @update:modelValue="handleConfirmPanelClose"/>
      </Transition>
      <Transition name="slide-left">
        <TransferOther v-show="selected === 'international'" :selectedAccount="selectedAccount"  @otp-verified="onOtpVerified"
                       @update-step="activeStep = $event"/>
      </Transition>

    </div>

    <!-- Transfer Buttons -->
<!--    <div class="flex justify-center gap-x-3 mt-10" v-if="!isPanelOpen && selected === 'european'">-->
<!--      <div class="w-full max-w-3xl">-->
<!--        &lt;!&ndash; General server error &ndash;&gt;-->
<!--        <p v-if="errors.general" class="text-sm text-red-500 mb-3">{{ errors.general }}</p>-->

<!--        <button class="w-full md:w-1/4 bg-primary text-white font-semibold py-2 px-6 rounded-md transition-colors text-lg tracking-wide shadow-md"-->
<!--                @click="submitTransfer();">-->
<!--          TRANSFER-->
<!--        </button>-->
<!--      </div>-->
<!--    </div>-->

    <div class="flex justify-center gap-x-3 mt-10" v-if="!isPanelOpen && selected === 'european'">
      <div class="w-full max-w-3xl">
        <!-- General server error -->
        <p v-if="errors.general" class="text-sm text-red-500 mb-3">{{ errors.general }}</p>

        <button
            class="w-full md:w-1/4 bg-primary text-white font-semibold py-2 px-6 rounded-md transition-colors text-lg tracking-wide shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isSubmitting"
            @click="submitTransfer"
        >
          <template v-if="!isSubmitting">
            TRANSFER
          </template>
          <template v-else>
            <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            Processing...
          </template>
        </button>
      </div>
    </div>

  </div>
</template>

<script setup>
import FloatingLabelInput from '@/components/ui/FloatingLabelInput.vue';
import FloatingLabelSelect from '@/components/ui/FloatingLabelSelect.vue';
import FloatingLabelFileInput from '@/components/ui/FloatingLabelFileInput.vue';
import BeneficiarySelector from '@/components/transfer/BeneficiarySelector.vue';
import {ref, watch, onMounted} from 'vue';
import {ArrowLeft, Euro, Globe, Trash} from 'lucide-vue-next';
import NewBeneficiaryModal from "@/components/transfer/NewBeneficiaryModal.vue";
import TransactionData from "@/components/transfer/TransactionData.vue";
import TransactionConfirm from "@/components/transfer/TransactionConfirm";
import TransferOther from "@/components/transfer/TransferOther";
import api from '@/services/api'; // axios instance
import debounce from 'lodash/debounce';
import {formatNumber} from "@/utils/number";
const fileInputKey = ref(0)
const transactionDataKey = ref(0);

// Form data
const searchBeneficiary = ref('')
const transferValue = ref('')
const description = ref('')
const additionalInfo = ref('')
const transferNature = ref('')
const iban = ref('')
const swiftCode = ref('')
const beneficiaryName = ref('')
const showDocumentForm = ref(false)
const documentDescription = ref('')
const fileInput = ref(null)

const isPanelOpen = ref(false)
const showTransactionConfirm = ref(false)
const isLoading = ref(false)
const activeStep = ref(1)
const isSubmitting = ref(false)

watch(activeStep, async (newVal) => {
  if (newVal === 3) {
    try {
      const res = await api.get('/accounts', {headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}});
      accounts.value = res.data
      if (accounts.value.length > 0) {
        selectedAccountId.value = accounts.value[0].id
        selectedAccount.value = accounts.value[0]
      }
    } catch (err) {
      console.error('Error fetching accounts or beneficiaries:', err);
    }
  }
})

// const items = [
//   {id:'european', label:'European Countries in Euros', icon:Euro},
//   {id:'international', label:'Other Transfers', icon:Globe},
// ]

const items = [
  { id: 'european', label: 'European Countries in Euros', icon: Euro, route: '/transfer' },
  { id: 'international', label: 'Other Transfers', icon: Globe, route: '/pay/state' }
]


const selected = ref('european')

const selectedBeneficiary = ref({})
const beneficiaryList = ref([])
const isModalOpen = ref(false)
const transactionId = ref(null); // این ID از سرور بعد از تایید OTP میاد
const toggleDocumentForm = (value) => showDocumentForm.value = value

const transactionData = ref({});
const otpStep = ref(false); // وقتی true شود، فرم OTP نمایش داده می‌شود
const transferValueError = ref('')

// errors reactive (for inline errors)
const errors = ref({})

// Validation for transfer value
const validateTransferValue = () => {
  const value = Number(transferValue.value)
  if (!value) {
    transferValueError.value = 'Please enter an amount'
  } else if (value < 10) {
    transferValueError.value = 'Minimum transfer amount is €10'
  } else if (value >= 50000) {
    transferValueError.value = 'Transfer amount must be less than €50,000'
  } else {
    transferValueError.value = '' // معتبر
  }
}


const submitTransfer = async () => {
  if (isSubmitting.value) return // جلوگیری از کلیک مجدد
  isSubmitting.value = true

  // reset errors
  errors.value = {}
  // validate numeric value
  validateTransferValue()
  if (transferValueError.value) {
    errors.value.transferValue = transferValueError.value
  }

  // بررسی موجودی
  if (selectedAccount.value && Number(transferValue.value) > Number(selectedAccount.value.available_balance)) {
    errors.value.transferValue = 'Insufficient balance for this transfer'
  }

  // field-by-field instead of alert
  if (!iban.value) errors.value.iban = 'IBAN is required'
  if (!swiftCode.value) errors.value.swift = 'SWIFT Code is required'
  if (!beneficiaryName.value) errors.value.beneficiaryName = 'Beneficiary Name is required'
  if (!transferValue.value) errors.value.transferValue = errors.value.transferValue || 'Value is required'
  if (!description.value) errors.value.description = 'Description is required'
  if (!transferNature.value) errors.value.transferNature = 'Nature of Fund Transfer is required'

  // if attach form open but no file added -> show error under document form
  if (showDocumentForm.value) {
    if (!documentDescription.value) {
      errors.value.documentDescription = 'Document description is required'
    }
    // fileInput is a ref to the component; if no attachedDocuments and no chosen file yet -> error
    // (we can't always access native input here, so rely on attachedDocuments state)
    if (!attachedDocuments.value.length) {
      errors.value.documentFile = 'Please select a file or cancel attaching'
    }
  }

  // if any errors -> stop and show inline
  if (Object.keys(errors.value).length > 0) {
    isSubmitting.value = false
    return
  }

  // آماده‌سازی داده‌ها
  transactionData.value = {
    account: selectedAccount.value,
    iban: iban.value,
    swift: swiftCode.value,
    beneficiaryName: beneficiaryName.value,
    value: transferValue.value,
    description: description.value,
    additionalInfo: additionalInfo.value,
    transferNature: transferNature.value,
    attachments: [...attachedDocuments.value] // اضافه کردن فایل‌ها
  }

  try {
    // ارسال درخواست OTP به سرور
    const res = await api.post('/transfer/send-otp', {
      account_id: selectedAccount.value?.id,
      iban: iban.value,
      swift: swiftCode.value,
      beneficiary_name: beneficiaryName.value,
      value: transferValue.value,
      description: description.value,
      additional_info: additionalInfo.value,
      transfer_nature: transferNature.value,
      transaction_type:'sepa_transfer'
    }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    console.log(res);

    if (res.data.success) {
      isPanelOpen.value = true;
      otpStep.value = true; // فرم OTP باز شود
      showTransactionConfirm.value = false; // تا تایید OTP انجام شود
      // transactionId.value = res.data.transaction_id; // ID تراکنش واقعی
      activeStep.value=2;
    } else {
      // show any server message inline
      errors.value.general = res.data.message || 'Failed to send OTP'
    }
  } catch (err) {
    // map server response errors to inline errors if present
    const data = err.response?.data
    if (data?.errors) {
      // if server returned field errors (array or object), merge them
      // normalize: Laravel typically returns object with arrays
      for (const key in data.errors) {
        const val = data.errors[key]
        // pick first message if array
        errors.value[key] = Array.isArray(val) ? val[0] : val
      }
    } else if (data?.message) {
      errors.value.general = data.message
    } else {
      errors.value.general = 'Failed to send OTP. Please try again.'
    }
    console.error('Error sending OTP:', err);
  }finally {
    isSubmitting.value = false
  }
}


function openNewBeneficiaryForm() { isModalOpen.value = true }
function onBeneficiarySelected(value) {
  selectedBeneficiary.value = value;

  // پر کردن فرم با اطلاعات Beneficiary انتخاب شده
  iban.value = value.iban || '';
  swiftCode.value = value.swift || '';
  beneficiaryName.value = value.name || '';
  description.value = value.description || '';
  additionalInfo.value = value.additional_info || '';
}



// -----------------
// Accounts logic
// -----------------
const accounts = ref([])
const selectedAccountId = ref(null)
const selectedAccount = ref(null)
// const searchQuery = ref('');
// const beneficiaryResults = ref([]);


onMounted(async () => {
  try {
    const res = await api.get('/accounts', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    accounts.value = res.data
    if(accounts.value.length>0){
      selectedAccountId.value = accounts.value[0].id
      selectedAccount.value = accounts.value[0]
    }
  } catch(err){
    console.error('Error fetching accounts or beneficiaries:', err);
  }
})

watch(selectedAccountId, (newId) => {
  selectedAccount.value = accounts.value.find(acc => acc.id === newId) || null
})

watch(isPanelOpen, (newVal) => {
  if (!newVal) {
    showTransactionConfirm.value = false;
    otpStep.value = false; // OTP Step ریست شود
  }
})

// Debounced search
const searchBeneficiaries = async () => {
  try {
    const query = searchBeneficiary.value;
    const res = await api.get('/beneficiaries/search', {
      params: {
        q: query.length >= 2 ? query : '', // اگر کمتر از 2 کاراکتر، جستجو خالی میشه
        account_type: selected.value
      },

      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    console.log("search",res.data);
    beneficiaryList.value = res.data;
  } catch (err) {
    console.error('Beneficiary search error:', err);
  }
};

const debouncedSearch = debounce(searchBeneficiaries, 300); // 300ms delay


onMounted(() => {
  searchBeneficiaries();
});


const transactionDataServer = ref(null);
const onOtpVerified = async (movement) => {
  otpStep.value = false;            // فرم OTP بسته شود
  showTransactionConfirm.value = true; // صفحه تایید تراکنش نمایش داده شود
  activeStep.value = 3;             // مرحله سوم فعال شود
  transactionDataServer.value = movement;


  try {
    const res = await api.get('/accounts', {headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}});
    accounts.value = res.data
    if (accounts.value.length > 0) {
      selectedAccountId.value = accounts.value[0].id
      selectedAccount.value = accounts.value[0]
    }
  } catch (err) {
    console.error('Error fetching accounts or beneficiaries:', err);
  }
};


const handleConfirmPanelClose = () => {
  isPanelOpen.value = false;
  showTransactionConfirm.value = false;
  otpStep.value = true;

  // ریست فرم انتقال
  transferValue.value = '';
  description.value = '';
  additionalInfo.value = '';
  transferNature.value = '';
  iban.value = '';
  swiftCode.value = '';
  beneficiaryName.value = '';
  selectedBeneficiary.value = {};
  transactionData.value = {};
  transactionId.value = null;
  activeStep.value=1;

  fileInputKey.value += 1;
  transactionDataKey.value += 1;
  attachedDocuments.value = [];
}


// File handling
const attachedDocuments = ref([]); // [{ file: File, description: String }]

// Sync with transactionData
watch(attachedDocuments, (newVal) => {
  transactionData.value.attachments = [...newVal]; // copy to ensure reactivity
}, { deep: true });

const handleFileSelect = (fileEventOrFile) => {
  // accept either native event (from input) or file object
  let f = null
  if (fileEventOrFile && fileEventOrFile.target && fileEventOrFile.target.files) {
    f = fileEventOrFile.target.files[0]
  } else {
    f = fileEventOrFile
  }
  if (!f) return;

  attachedDocuments.value.push({
    file: f,
    description: documentDescription.value || ''
  });

  documentDescription.value = '';
  showDocumentForm.value = false;
};


const removeDocument = (index) => {
  attachedDocuments.value.splice(index, 1);
};
</script>
